import { HttpClient, HttpEvent, HttpHeaders, HttpRequest, HttpResponse, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ConfigurationService } from '../configuration/configuration.service';
import { catchError, timeout, retryWhen, scan, delay, filter, map } from 'rxjs/operators';
import { LoggerService } from '../logger-services/logger.service';
import { ApiService } from '../api/api.service';
import { GlobalConfigService } from '../global-service/global-service.service';
import { ChatData } from '../../app/models/chatbot.models';
import { FileStatusTrackerData } from '../../app/models/file-upload.models';
import { TokenService } from '../../shared/services/token/token.service';
import { RegulationNextDownloadService } from '../regulation-next-download/regulation-next-download.service';
@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  public baseUrl = environment.baseUrl;
  public tenantID: any;
  configdata: any;
  private readonly x_api_key = "3acf63d176cd0be21c9f66d720712686949ac39844356cbc187ade4ce5b2bce4faaff12aabcacb286";
  private ws: WebSocket | null = null;
  private responseSubject: Subject<any> | null = null;
  private isAuthenticated: boolean = false;
  private base_payload: any;
  private chatStreamRequestId: string = ''; // Request ID per chat WebSocket connection

  // File Status Tracker WebSocket properties
  private fileStatusTracker: WebSocket | null = null;
  private fileStatusTrackerSubject: Subject<any> | null = null;
  private isFileStatusTrackerAuthenticated: boolean = false;
  private fileStatusTrackerRequestId: string = ''; // Request ID per file tracker WebSocket connection
  
  // News WebSocket properties
  private newsWs: WebSocket | null = null;
  private newsResponseSubject: Subject<any> | null = null;
  private isNewsAuthenticated: boolean = false;
  private news_base_payload: any;
  private newsRequestId: string = ''; // Request ID per news WebSocket connection

  public enhancementPrompts: { [key: string]: string } = {};
  private readonly ChatbotServiceError = (error: any)=>
    `[ChatbotService] ${JSON.stringify(error)}`;

  // Modern API service injection
  private readonly apiService = inject(ApiService);

  /**
   * Wraps API response data in HttpResponse format for backward compatibility with existing components
   */
  private wrapResponseForCompatibility<T>(body: T, status: number = 200): HttpResponse<T> {
    return {
      body,
      status,
      statusText: status === 200 ? 'OK' : 'Error',
      headers: null as any,
      url: null,
      ok: status >= 200 && status < 300,
      type: 4,
      clone: function() { return this; }
    } as HttpResponse<T>;
  }

  // Global config service injection for request ID generation
  private readonly globalConfigService = inject(GlobalConfigService);

  // Token service injection for fresh access token
  private readonly tokenService = inject(TokenService);

  private readonly regulationNextDownloadService = inject(RegulationNextDownloadService);

  /**
   * Constructor for ChatbotService
   * @param http - Angular HttpClient for making HTTP requests
   * @param configService - Configuration service for app settings
   * @param loggerService - Logger service for error logging
   */
  constructor(private readonly http: HttpClient,
     private readonly configService: ConfigurationService,
     private readonly loggerService: LoggerService
  ) {
    this.configdata = configService.config;
    this.baseUrl = this.configdata.apiBaseUrl;
    environment.baseUrl = this.baseUrl;
    environment.chatbotName = this.configdata.chatbotName;
    this.tenantID = environment.TenantId;
  }

  /**
   * Get page count from PDF or DOCX files using the backend API
   * @param file The file to get page count for
   * @param tenantId Tenant ID for access control
   * @param userId User ID for access control
   * @returns Promise<number> The number of pages in the file
   */
  async getPageCount(file: File, docId: string, tenantId: string, userId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Content = (reader.result as string).split(',')[1];
          
          const requestBody = {
            doc_id: docId,
            file_content: base64Content,
            file_name: file.name,
            tenant_id: tenantId,
            user_id: userId
          };

          const response = await this.apiService.post<any>(
            this.apiService.getApiUrlFromConfig('GET_PAGE_COUNT'), 
            requestBody, 
            this.tokenService.getAccessToken()
          ).toPromise();
          resolve(response.page_count);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }
  /**
   * Gets the configuration data
   * @returns The configuration data object
   */
  getConfigData() {
    return this.configdata;
  }

  /**
   * Gets popular questions from JSON configuration file
   * @returns Observable containing popular questions data
   */
  getJSON(): Observable<any> {
    return this.http.get('config/PopularQuestion.json');
  }

  /**
   * Retrieves chat sessions for a user
   * @param userID - The user identifier
   * @param tenantID - The tenant identifier
   * @param DomainID - The domain identifier
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of items per page (default: 10)
   * @param startDate - Optional start date filter
   * @param endDate - Optional end date filter
   * @param version - API version (default: 1.0)
   * @param modules - Array of module names
   * @param isDomainManagementEnabled - Whether domain management is enabled
   * @returns Observable containing chat sessions response
   */
  GetChatSessions(userID: any, tenantID: any, DomainID: any, page: number = 1, pageSize: number = 10, startDate?: string, endDate?: string, version: number = 1.0, modules: string[] = [], isDomainManagementEnabled: boolean = false) {
    try{
    let basePayload = {
      tenant_id  : tenantID,
      user_id : userID
    };
    let data = {};
    if (version === 1.0) {
      data = {...basePayload, selected_domains: DomainID, version: version}
    } else {
      if(!isDomainManagementEnabled){
        data = {...basePayload, modules: modules,start_date: startDate,end_date: endDate,selected_domains: DomainID,page: page,page_size: pageSize}
      }
      else{
        data = {...basePayload, modules: modules,start_date: startDate,end_date: endDate,page: page,page_size: pageSize}
      }
    }
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GET_THREADS'), data, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "GetChatSessions")
    return of();
  }
  }

  /**
   * Deletes a chat session
   * @param SessionID - The session identifier to delete
   * @param LoginUserID - The logged in user identifier
   * @param tenantID - The tenant identifier
   * @param domainId - The domain identifier
   * @returns Observable containing deletion response
   */
  DeleteChatSession(SessionID: any, LoginUserID: any, tenantID: any, domainId: any) {
    try{
    let data = {
      thread_id: SessionID,
      user_id: LoginUserID,
      tenant_id : tenantID
    };
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('DELETE_THREAD'), data, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "DeleteChatSession")
    return of();
  }
  }

  /**
   * Updates the name of a chat session
   * @param SessionID - The session identifier to update
   * @param ChatName - The new name for the chat session
   * @param UserID - The user identifier
   * @param tenantID - The tenant identifier
   * @param domainId - The domain identifier
   * @returns Observable containing update response
   */
  UpdateSessionName(SessionID: any, ChatName: any, UserID: any, tenantID: any, domainId: any): Observable<any> {
    try{
    let data = {
      thread_id: SessionID,
      tenant_id : tenantID,
      thread_name: ChatName,
      user_id: UserID
    };
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('UPDATE_THREAD_NAME'), data, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
    catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "UpdateSessionName")
    return of({});
  }
  
  }

  /**
   * Gets chat response from the chatbot API
   * @param data - The chat request data
   * @returns Observable containing chat response
   */
  getChatResponse(data: any): Observable<any> {
    try{
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GET_CHAT_BOT'), data, this.tokenService.getAccessToken());
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "getChatResponse")
    return of({});
  }
  }

  /**
   * Retrieves chat history from the API
   * @param data - The request data for chat history
   * @returns Observable containing chat history response
   */
  GetChatHistoryAPI(data: any): Observable<any> {
    try{
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GET_CHAT_RESPONSE'), data, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "GetChatHistoryAPI")
    return of({});
  }
  }

  /**
   * Gets dynamic popular questions for a user and domain
   * @param userID - The user identifier
   * @param DomainID - The domain identifier
   * @returns Observable containing popular questions
   */
  GetDynamicPopularQuestions(userID: any, DomainID: any) {
    const params = new HttpParams()
      .set('userID', userID)
      .set('DomainID', DomainID)
      .set('tenantID', this.tenantID);
    
    return this.apiService.get<any>(this.apiService.getApiUrlFromConfig('GET_DYNAMIC_POPULAR_QUESTIONS'), this.tokenService.getAccessToken(), params);
  }

  /**
   * Calls the chunked API v2 using WebSocket for streaming responses
   * @param rdata - The chat data containing question and session information
   * @returns Observable containing streaming chat responses
   */
  callChunkedApiV2(rdata: ChatData): Observable<any> {
    try{
    this.responseSubject = new Subject<any>();
    const url = this.configdata.websocket_url + "/chat_stream";

    // Generate unique request ID only if this is a new connection (no existing WebSocket or closed WebSocket)
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED || !this.chatStreamRequestId) {
      this.chatStreamRequestId = this.globalConfigService.generateRequestId();
    }

    // Authentication payload (to be sent first)
    // Format: {tenant_id, user_id, thread_id, available_domains, modules, selected_domains, token}
    const rdataAny = rdata as any; // Type assertion for WebSocket-specific fields
    const auth_payload = {
      "tenant_id": rdata.tenantID || String(rdata.tenantID || ''),
      "user_id": rdata.UserId || String(rdata.UserId || ''),
      "thread_id": rdata.SessionId && rdata.SessionId.length > 0 ? rdata.SessionId[0] : '',
      "available_domains": Array.isArray(rdataAny.available_domains) ? rdataAny.available_domains : (rdataAny.available_domains ? [rdataAny.available_domains] : []),
      "modules": Array.isArray(rdataAny.modules) ? rdataAny.modules : (rdata.ModuleName ? [String(rdata.ModuleName).toLowerCase()] : []),
      "selected_domains": Array.isArray(rdataAny.selected_domains) ? rdataAny.selected_domains : [],
      "token": rdata.Access_Token || '',
      "request_id": this.chatStreamRequestId
   };


    this.base_payload = {
      "question": rdata.Question || '',
      "tenant_id": rdata.tenantID || String(rdata.tenantID || ''),
      "user_id": rdata.UserId || String(rdata.UserId || ''),
      "thread_id": rdata.SessionId && rdata.SessionId.length > 0 ? rdata.SessionId[0] : '',
      "available_domains": Array.isArray(rdataAny.available_domains) ? rdataAny.available_domains : (rdataAny.available_domains ? [rdataAny.available_domains] : []),
      "countries": Array.isArray(rdataAny.countries) ? rdataAny.countries : (Array.isArray(rdata.Markets) ? rdata.Markets : []),
      "modules": Array.isArray(rdataAny.modules) ? rdataAny.modules : (rdata.ModuleName ? [String(rdata.ModuleName).toLowerCase()] : []),
      "selected_domains": Array.isArray(rdataAny.selected_domains) ? rdataAny.selected_domains : [],
      "request_id": this.chatStreamRequestId
    };

    if (rdataAny.retrieval_mode === 'latest' || rdataAny.retrieval_mode === 'all') {
      this.base_payload.retrieval_mode = rdataAny.retrieval_mode;
    }

    // Question payload with rendered flag
    const question_payload = {
      ...this.base_payload,
      "rendered": false
    };
  

    const connect = () => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          if (this.isAuthenticated) {
              this.ws.send(JSON.stringify(question_payload));
          } else {
              this.ws.send(JSON.stringify(auth_payload));
          }
          return;
      }
      this.ws = new WebSocket(url);
      let retryCount = 0;
      const maxRetries = 3;
      const retryDelay = 1000;

      this.ws.onopen = () => {
        retryCount = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          // Handle specific response types
          if (response.status === "awaiting_auth") {
            // Send authentication message first
            this.ws?.send(JSON.stringify(auth_payload));
            return;
        }

        if (response.status === "auth_failed" || response.status === "auth_timeout") {
          this.responseSubject?.error(new Error(`Authentication error: ${response.message}`));
          return;
        }

        if (response.status === "connected") {
          this.isAuthenticated = true;
          if (this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify(question_payload));
          }
          return;
        }

        if (response.status === "error") {
          this.responseSubject?.error(new Error(`Processing error: ${response.message}`));
          return;
        }

        // Forward all responses to subscribers
        this.responseSubject?.next(response);
        }
        catch (error: any) {
          this.loggerService.logError(this.ChatbotServiceError(error), "callChunkedApiV2", undefined, undefined, undefined, this.chatStreamRequestId)
          this.responseSubject?.error(error);
        }
      };

      this.ws.onerror = (error) => {  
        this.loggerService.logError(this.ChatbotServiceError(error), "callChunkedApiV2", undefined, undefined, undefined, this.chatStreamRequestId)
        if (retryCount < maxRetries && !this.isAuthenticated) {
          retryCount++;
          setTimeout(connect, retryDelay);
        } else {
          this.responseSubject?.error(new Error('WebSocket error: Connection failed after retries'));
        }
      };
    }
  connect();
  return this.responseSubject?.asObservable();
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "callChunkedApiV2")
    return of({});
  }
  }


  /**
   * Sends a WebSocket message to save chat history
   */
  sendWebSocketMessage() {
    if (!this.base_payload || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    // Ensure we have a valid request ID before sending
    if (!this.chatStreamRequestId) {
      this.chatStreamRequestId = this.globalConfigService.generateRequestId();
    }
    const save_history_payload: Record<string, unknown> = {
      "question": this.base_payload.question,
      "tenant_id": this.base_payload.tenant_id,
      "user_id": this.base_payload.user_id,
      "request_id": this.chatStreamRequestId,
      "thread_id": this.base_payload.thread_id,
      "modules": this.base_payload.modules,
      "selected_domains": this.base_payload.selected_domains,
      "countries": this.base_payload.countries,
      "available_domains": this.base_payload.available_domains,
      "rendered": true
    };
    if (this.base_payload.retrieval_mode === 'latest' || this.base_payload.retrieval_mode === 'all') {
      save_history_payload['retrieval_mode'] = this.base_payload.retrieval_mode;
    }
    this.ws.send(JSON.stringify(save_history_payload));
  }
  
  /**
   * Closes the WebSocket connection and cleans up resources
   */
  closeWebSocket(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
      this.ws = null;
      this.responseSubject?.complete();
      this.base_payload = null;
      this.chatStreamRequestId = ''; // Clear request ID when connection closes
    }
    else {
      this.loggerService.logError("WebSocket connection not open","closeWebSocket")
    }
  }

  /**
   * Checks if the WebSocket connection is open
   * @returns True if WebSocket is open, false otherwise
   */
  isWebSocketOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Ensures WebSocket connection is established and authenticated
   * @param tenantId - Tenant ID
   * @param userId - User ID
   * @param accessToken - Access token
   * @returns Promise that resolves when connection is authenticated
   */
  private async ensureFileStatusTrackerConnected(tenantId: string, userId: string, accessToken: string): Promise<void> {
    // Generate request ID only if this is a new connection (no existing WebSocket or closed WebSocket)
    if (!this.fileStatusTracker || this.fileStatusTracker.readyState === WebSocket.CLOSED || !this.fileStatusTrackerRequestId) {
      this.fileStatusTrackerRequestId = this.globalConfigService.generateRequestId();
    }
    
    const url = `${this.configdata.fileStatusUrl}/v2/api/track_file_uploading_status`;
    
    // If already connected and authenticated, return immediately
    if (this.fileStatusTracker && 
        this.fileStatusTracker.readyState === WebSocket.OPEN && 
        this.isFileStatusTrackerAuthenticated) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {

      const fileStatusTracker_auth_payload = {
        "tenant_id": tenantId,
        "user_id": userId,
        "access_token": accessToken,
        "request_id": this.fileStatusTrackerRequestId
      };

      // Create shared subject if it doesn't exist
      if (!this.fileStatusTrackerSubject) {
        this.fileStatusTrackerSubject = new Subject<any>();
      }

      let retryCount = 0;
      const maxRetries = 3;
      const retryDelay = 1000;

      const connect = () => {
        this.fileStatusTracker = new WebSocket(url);

        this.fileStatusTracker.onopen = () => {
          retryCount = 0;
          this.loggerService.logInfo("FileStatusTracker WebSocket opened","ensureFileStatusTrackerConnected");
        };

        this.fileStatusTracker.onmessage = (event) => {
          try {
            const response = JSON.parse(event.data);
            this.loggerService.logInfo(`FileStatusTracker message: ${JSON.stringify(response)}`,"ensureFileStatusTrackerConnected");

            if (response.status === "awaiting_auth") {
              if (this.fileStatusTracker?.readyState === WebSocket.OPEN) {
                this.fileStatusTracker.send(JSON.stringify(fileStatusTracker_auth_payload));
              }
              return;
            }

            if (response.status === "auth_failed") {
              const error = new Error(`File Status Tracker authentication failed: ${response.message}`);
              this.fileStatusTrackerSubject?.error(error);
              reject(error);
              return;
            }

            if (response.status === "connected") {
              this.isFileStatusTrackerAuthenticated = true;
              this.loggerService.logInfo("FileStatusTracker authenticated successfully","ensureFileStatusTrackerConnected");
              resolve(); // Resolve the promise when authenticated
              return;
            }

            if (response.status === "error") {
              const error = new Error(`File Status Tracker error: ${response.message}`);
              this.fileStatusTrackerSubject?.error(error);
              return;
            }

            // Forward all file status responses to all subscribers
            this.fileStatusTrackerSubject?.next(response);

          } catch (error: any) {
            this.loggerService.logError(this.ChatbotServiceError(error), "FileStatusTracker");
            this.fileStatusTrackerSubject?.error(error);
            reject(error);
          }
        };

        this.fileStatusTracker.onerror = (error) => {
          this.loggerService.logError(this.ChatbotServiceError(error), "FileStatusTracker");
          if (retryCount < maxRetries) {
            retryCount++;
            this.loggerService.logInfo(`Retrying FileStatusTracker connection (${retryCount}/${maxRetries})...`,"ensureFileStatusTrackerConnected");
            setTimeout(connect, retryDelay);
          } else {
            const error = new Error('File Status Tracker WebSocket error: Connection failed after retries');
            this.fileStatusTrackerSubject?.error(error);
            reject(error);
          }
        };

        this.fileStatusTracker.onclose = () => {
          this.loggerService.logInfo("FileStatusTracker WebSocket closed","ensureFileStatusTrackerConnected");
          this.isFileStatusTrackerAuthenticated = false;
        };
      };

      connect();
    });
  }

  /**
   * Tracks the status of a file upload
   * @param docData - The file data containing file information
   * @returns Observable containing file status tracker response
   */
  async FileStatusTracker(docData: FileStatusTrackerData): Promise<Observable<any>> {
    try {
      this.loggerService.logInfo(`FileStatusTracker called with docData: ${JSON.stringify(docData)}`,"FileStatusTracker", undefined, undefined, undefined, this.fileStatusTrackerRequestId);

      // Ensure connection is established and authenticated (will generate request ID if needed)
      await this.ensureFileStatusTrackerConnected(
        docData.tenantId,
        docData.userId,
        docData.accssToken || ''
      );

      // Only send tracking payload if we have valid doc_id (not a connection-only call)
      if (docData.docId && docData.docName && docData.s3Key) {
        const fileStatusTracker_base_payload = {
          "tenant_id": docData.tenantId,
          "user_id": docData.userId,
          "doc_id": docData.docId,
          "doc_name": docData.docName,
          "s3_url": docData.s3Key,
          "presigned_url": docData.presignedUrl || '',
          "doc_type": docData.docType || '',
          "doc_size": docData.docSize || 0,
          "request_id": this.fileStatusTrackerRequestId
        };

        this.loggerService.logInfo(`Sending file tracking payload: ${JSON.stringify(fileStatusTracker_base_payload)}`,"FileStatusTracker", undefined, undefined, undefined, this.fileStatusTrackerRequestId);

        if (this.fileStatusTracker?.readyState === WebSocket.OPEN) {
          this.fileStatusTracker.send(JSON.stringify(fileStatusTracker_base_payload));
        } else {
          throw new Error("WebSocket connection not open");
        }

        // Return observable that filters messages for this specific doc_id
        return this.fileStatusTrackerSubject!.asObservable().pipe(
          filter((response: any) => response.doc_id === docData.docId)
        );
      } else {
        // Connection-only call (no payload to send)
        this.loggerService.logInfo("Connection established, no tracking payload sent","FileStatusTracker", undefined, undefined, undefined, this.fileStatusTrackerRequestId);
        return this.fileStatusTrackerSubject!.asObservable();
      }

    } catch (error: any) {
      this.loggerService.logError(this.ChatbotServiceError(error), "FileStatusTracker", undefined, undefined, undefined, this.fileStatusTrackerRequestId);
      return of({});
    }
  }

  /**
   * Checks if the file status tracker WebSocket connection is open
   * @returns True if file status tracker WebSocket is open, false otherwise
   */
  isFileStatusTrackerWebSocketOpen(): boolean {
    return this.fileStatusTracker !== null && this.fileStatusTracker.readyState === WebSocket.OPEN;
  }
  

  /**
   * Closes the file status tracker WebSocket connection and cleans up resources
  */
  closeFileStatusTrackerWebSocket(): void {
    try{
      if (this.fileStatusTracker && this.fileStatusTracker.readyState === WebSocket.OPEN) {
        this.fileStatusTracker.close();
        this.fileStatusTracker = null;
        this.fileStatusTrackerSubject?.complete();
        this.isFileStatusTrackerAuthenticated = false;
        this.fileStatusTrackerRequestId = ''; // Clear request ID when connection closes
      }
      else {
        this.loggerService.logError("File Status Tracker WebSocket connection not open","closeFileStatusTracker")
      }
    } 
    catch(error: any){  
      this.loggerService.logError(this.ChatbotServiceError(error), "closeFileStatusTracker")
    }
  }

  // Call the news updates WebSocket with the provided ChatData

  /**
   * Calls the news updates WebSocket for streaming news data
   * @param rdata - The chat data containing session and domain information
   * @returns Observable containing streaming news updates
   */
  callNewsUpdatesWebSocket(rdata: ChatData): Observable<any> {
    try{
    this.newsResponseSubject = new Subject<any>();
    const url = this.configdata.websocket_url + "/news_and_updates"; // Use different endpoint for news

    // Generate unique request ID only if this is a new connection (no existing WebSocket or closed WebSocket)
    if (!this.newsWs || this.newsWs.readyState === WebSocket.CLOSED || !this.newsRequestId) {
      this.newsRequestId = this.globalConfigService.generateRequestId();
    }

    // Authentication payload for news auth traceability
    const news_auth_payload = {
      "tenant_id": rdata.tenantID,
      "user_id": rdata.UserId,
      "session_id": rdata.SessionId[0],
      "domain_id": rdata.ModuleDisplayName === "RTQ" ? "1" : `${rdata.DomainId}`,
      "token": rdata.Access_Token,
      "request_id": this.newsRequestId
    };

    this.news_base_payload = {
      "tenant_id": rdata.tenantID,
      "user_id": rdata.UserId,
      "session_id": rdata.SessionId[0],
      "domain_id": rdata.ModuleDisplayName === "RTQ" ? "1" : `${rdata.DomainId}`,
      "token": rdata.Access_Token,
      "request_id": this.newsRequestId
    };

    const news_request_payload = {
      ...this.news_base_payload,
      "rendered": 0,
      "question": rdata.Question,
    };

    const connectNews = () => {
      if (this.newsWs && this.newsWs.readyState === WebSocket.OPEN) {
        if (this.isNewsAuthenticated) {
          this.newsWs.send(JSON.stringify(news_request_payload));
        } else {
          this.newsWs.send(JSON.stringify(news_auth_payload));
        }
        return;
      }

      this.newsWs = new WebSocket(url);
      let retryCount = 0;
      const maxRetries = 3;
      const retryDelay = 1000;

      this.newsWs.onopen = () => {
        retryCount = 0;
      };

      this.newsWs.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          
          // Handle specific response types for news WebSocket
          if (response.status === "awaiting_auth") {
            this.newsWs?.send(JSON.stringify(news_auth_payload));
            return;
          }

          if (response.status === "auth_failed" || response.status === "auth_timeout") {
            this.newsResponseSubject?.error(new Error(`News Authentication error: ${response.message}`));
            return;
          }

          if (response.status === "connected") {
            this.isNewsAuthenticated = true;
            if (this.newsWs?.readyState === WebSocket.OPEN) {
              this.newsWs.send(JSON.stringify(news_request_payload));
            }
            return;
          }

          if (response.status === "error") {
            this.newsResponseSubject?.error(new Error(`News Processing error: ${response.message}`));
            return;
          }

          // Forward all news responses to subscribers
          this.newsResponseSubject?.next(response);
        } catch (error: any) {
          this.loggerService.logError(this.ChatbotServiceError(error), "callNewsUpdatesWebSocket", undefined, undefined, undefined, this.newsRequestId)
          this.newsResponseSubject?.error(error);
        }
      };

      this.newsWs.onerror = (error) => {
        if (retryCount < maxRetries && !this.isNewsAuthenticated) {
          retryCount++;
          setTimeout(connectNews, retryDelay);
        } else {
          this.newsResponseSubject?.error(new Error('News WebSocket error: Connection failed after retries'));
        }
      };

      this.newsWs.onclose = (event) => {
        this.isNewsAuthenticated = false;
      };
    };

    connectNews();
    return this.newsResponseSubject?.asObservable();
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "callNewsUpdatesWebSocket")
    return of({});
  }
  }

  /**
   * Sends a news WebSocket message with chat history and news data
   * @param pk_chat_history_id - The primary key of chat history
   * @param fk_chatbotid - The foreign key of chatbot session
   * @param newsAndUpdates - The news and updates data to send
   */
  sendNewsWebSocketMessage(pk_chat_history_id: any,fk_chatbotid: any,newsAndUpdates: any) {
    if (!this.news_base_payload || !this.newsWs || this.newsWs.readyState !== WebSocket.OPEN) {
      this.loggerService.logError("Cannot send news message: News WebSocket not open or payload missing","sendNewsWebSocketMessage")
      return;
    }
    
    // Validate required fields when rendered is 1
    const chatHistoryId = pk_chat_history_id ? pk_chat_history_id.toString() : null;
    const sessionId = fk_chatbotid ? fk_chatbotid.toString() : null;
    
    // Check if we have valid data for rendered request
    const hasValidData = chatHistoryId && newsAndUpdates && 
      (Array.isArray(newsAndUpdates) ? newsAndUpdates.length > 0 : newsAndUpdates);

    if (!hasValidData) {
      return;
    }
    
    try {
      // Ensure newsAndUpdates is properly serializable
      let serializableNewsAndUpdates;
      
      serializableNewsAndUpdates = JSON.parse(JSON.stringify(newsAndUpdates));
      
      const news_message_payload = {
        "chat_history_id": chatHistoryId,
        "fk_chatbotid": sessionId || "",
        "news_and_feeds": serializableNewsAndUpdates,
        "rendered": 1,
      };
      
      // Test serialization before sending
      const serializedPayload = JSON.stringify(news_message_payload);
      this.newsWs.send(serializedPayload);
      
    } catch (error: any) {
        this.loggerService.logError(this.ChatbotServiceError(error), "sendNewsWebSocketMessage")
      // Try sending without the problematic data
      const fallbackPayload = {
        "chat_history_id": chatHistoryId,
        "fk_chatbotid": sessionId || "",
        "news_and_feeds": [],
        "rendered": 1,
      };
      this.newsWs.send(JSON.stringify(fallbackPayload));
    }
  }

  /**
   * Closes the news WebSocket connection and cleans up resources
   */
  closeNewsWebSocket(): void {
    if (this.newsWs && this.newsWs.readyState === WebSocket.OPEN) {
      this.newsWs.close();
      this.newsWs = null;
      this.newsResponseSubject?.complete();
      this.news_base_payload = null;
      this.isNewsAuthenticated = false;
      this.newsRequestId = ''; // Clear request ID when connection closes
    }
    else {
      this.loggerService.logError("News WebSocket connection not open","closeNewsWebSocket")
    }
  }

  /**
   * Checks if the news WebSocket connection is open
   * @returns True if news WebSocket is open, false otherwise
   */
  isNewsWebSocketOpen(): boolean {
    return this.newsWs !== null && this.newsWs.readyState === WebSocket.OPEN;
  }


  /**
   * Checks if both WebSocket connections are open
   * @returns True if both WebSockets are open, false otherwise
   */
  AllWebSocketsConnectionsOpen(): boolean {
    return this.isWebSocketOpen() && this.isNewsWebSocketOpen() && this.isFileStatusTrackerWebSocketOpen();
  }

  /**
   * Closes both WebSocket connections
   */
  closeAllWebSockets(): void {
    this.closeWebSocket();
    this.closeNewsWebSocket();
    this.closeFileStatusTrackerWebSocket();
  }

  /**
   * Calls the chunked API for streaming responses (legacy method)
   * @param rdata - The request data for chat processing
   * @returns Observable containing HTTP events for streaming response
   */
  callChunkedApi(rdata: any): Observable<HttpEvent<any>> {
    try{
    const url = this.configdata.StreamResponseURL + this.configdata.StreamResponseSubURL;

    let data = {
      "question": rdata.Question,
      "module_name": rdata.ModuleName,
      "chat_history": rdata.ChatHistory,
      "countries": rdata.Markets,
      "prompt": rdata.prompt,
      "ModuleDisplayName": rdata.ModuleDisplayName,
      "SubModuleName": rdata.SubModuleName,
      "SessionModuleName": rdata.SessionModuleName,
      "tenant_id": rdata.tenantID,
      "privileges": rdata.Privileges,
      "user_id": rdata.UserId,
      "session_id": rdata.SessionId[0],
      "domain_name": rdata.ModuleDisplayName === "RTQ" ? "MPR" : rdata.DomainName,
      "domain_id": rdata.ModuleDisplayName === "RTQ" ? "1" : `${rdata.DomainId}`,
      "token": rdata.Access_Token
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.x_api_key.toLowerCase() // ensure the API key variable is also in lowercase
    });

    const request = new HttpRequest('POST', url, data, {
      headers: headers,
      reportProgress: true,
      responseType: 'text',
    });

    let response = this.http.request(request).pipe(
      timeout(90000),
      retryWhen(errors =>
        errors.pipe(
          scan((acc, _value) => {
            if (acc >= 1) {
              throw new Error('Maximum retry attempts reached, exiting...');
            }
            return acc + 1;
          }, 0),
          delay(1000)
        )
      ),
      catchError(error => {
        throw error;
      })
    );

    return response;
    
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "callChunkedApi")
    return of({} as HttpEvent<any>);
  }
  }

  /**
   * Saves chat response to the backend
   * @param data - The chat response data to save
   * @returns Observable containing save operation response
   */
  saveChatResponse(data: any): Observable<any> {
    try{
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('SAVE_CHAT_RESPONSE'), data, this.tokenService.getAccessToken())
      .pipe(
        map((response: any) => this.wrapResponseForCompatibility(response)),
        catchError((error: HttpErrorResponse) => {
          const status = error.status ? error.status.toString() : 'Unknown';
          if (!navigator.onLine) {
            return this.handleError('309', "Internet connection lost. Please check your network and try again.", JSON.stringify(error), "CheckChatbotAPIHealth DS API");
          } else {
            return this.handleError(status, "Error saving chat history. Please try again later or contact support if the issue persists.", JSON.stringify(error), "saveChatResponse");
          }
        })
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "saveChatResponse")
    return of({});
  }
  }

  /**
   * Creates a standardized error response object
   * @param status - The error status code
   * @param errorMessage - The error message to display
   * @param errorDetail - Detailed error information
   * @param methodName - The name of the method where error occurred
   * @returns Error response object
   */
  createErrorResponse(
    status: string,
    errorMessage: string,
    errorDetail: any,
    methodName: string,
  ) {
    return {
      "status": status,
      "error_message": errorMessage,
      "error": errorDetail,
      "recData": []
    };
  }

  /**
   * Handles errors and returns a standardized error response
   * @param status - The error status code
   * @param errorMessage - The error message to display
   * @param errorDetail - Detailed error information
   * @param methodName - The name of the method where error occurred
   * @param error - The HTTP error response object
   * @returns Observable containing error response
   */
  handleError(
    status: string,
    errorMessage: string,
    errorDetail: any,
    methodName: string,
    error: HttpErrorResponse = new HttpErrorResponse({}),
  ): Observable<any> {
    this.loggerService.logError(this.ChatbotServiceError(error), "handleError")
    return of({
      "status": status,
      "error_message": errorMessage,
      "error": errorDetail,
      "recData": []
    });
  }


  /**
   * Gets the document chat URL for a specific file
   * @param fileName - The name of the file
   * @param insight_id - The insight identifier
   * @returns Observable containing document chat URL
   */
  GetDocChatUrl(fileName: any,insight_id:any) {
    const params = new HttpParams()
      .set('fileName', fileName)
      .set('tenantId', this.tenantID)
      .set('insight_id', insight_id);
    
    // Construct the custom URL using RIApiUrl and DocChatUrl from config
    const customUrl = `${this.configdata.RIApiUrl}${this.configdata.DocChatUrl}${fileName}&tenantId=${this.tenantID}&insight_id=${insight_id}`;
    
    return this.apiService.get<any>(
      '', // empty endpoint since we're using full custom URL
      this.tokenService.getAccessToken(),
      undefined,
      'COMMON',
      customUrl
    );
  }


  /**
   * Views or downloads an insight file
   * @param fileName - The name of the file to view
   * @param tenantId - The tenant identifier (default: 0)
   * @param insight_id - The insight identifier
   * @returns Observable containing file data
   */
  ViewInsightFile(fileName: string, tenantId: any = 0, insight_id: any): Observable<any> {
    try{
    if (this.regulationNextDownloadService.hasGriRegulationAccess()) {
      return this.regulationNextDownloadService.getPresignedUrl(fileName);
    }
    if (this.configdata.DownloadApiToggle) {
      // New API URL (V3) - POST with text response
      const endpoint = this.apiService.getApiUrlFromConfig('VIEW_INSIGHT_FILE_V3');
      const requestBody = {
        tenantId: tenantId,
        fileName: fileName,
        sourcePath: ""
      };
      const customUrl = `${this.configdata.RIApiUrl}/regulations/Regulations/v3.0/DownloadFile`;
      
      return this.apiService.post<any>(
        endpoint,
        requestBody,
        this.tokenService.getAccessToken(),
        'COMMON',
        this.configdata.RIApiUrl,
        'text'
      );
    }
    else {
      // Old API URL (V2) - GET with blob response
      const params = new HttpParams()
        .set('tenantId', tenantId)
        .set('fileName', fileName)
        .set('insight_id', insight_id);
      
      const endpoint = this.apiService.getApiUrlFromConfig('VIEW_INSIGHT_FILE_V2');
      const customUrl = `${this.configdata.RIApiUrl}/gateway/Regulations/v2.0/DownloadFile`;
      
      return this.apiService.get<any>(
        customUrl,
        this.tokenService.getAccessToken(),
        params,
        'COMMON',
        undefined,
        'blob'
      );
    }
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "ViewInsightFile")
    return of({});
  }
  }

  /**
   * Updates feedback for a chat response (like/dislike)
   * @param chatHistoryId - The chat history identifier
   * @param IsLike - Whether the response is liked (true/false/null)
   * @param jsonDict - Additional feedback data
   * @param additionalComments - User comments for feedback
   * @param isReset - Whether to reset the feedback
   * @returns Observable containing feedback update response
   */
  UpdateDisLikeFeedback(chatHistoryId: number, IsLike: boolean | null,jsonDict: any, additionalComments: string, isReset: boolean) {
    try{
    // Handle reset case or use provided values
    const finalIsLike = isReset ? null : IsLike;
    const finalFeedbackItems = isReset ? null : jsonDict;
    const finalComments = isReset ? null : additionalComments;
  
    // Build base payload
    const payload = {
      response_id: chatHistoryId,
      is_like: finalIsLike,
      tenant_id: this.tenantID,
      // Only include feedback and comments for non-positive feedback
      ...(finalIsLike !== true && { 
        feedback: finalFeedbackItems, 
        comments: finalComments 
      })
    };
    
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('SAVE_RESPONSE_FEEDBACK'), payload, this.tokenService.getAccessToken(), this.apiService.getApiServiceFromConfig('SAVE_RESPONSE_FEEDBACK'))
      .pipe(
        map(response => ({ body: response, status: 200, statusText: 'OK' })) // Wrap response to maintain compatibility
      );
  }
  catch(error:any){
    this.loggerService.logError(this.ChatbotServiceError(error), "UpdateDisLikeFeedback")
    return of();
  }
  }

  /**
   * Gets chat session data for a specific user and session
   * @param user_id - The user identifier
   * @param session_id - The session identifier
   * @param file_name - The file name associated with the session
   * @returns Observable containing chat session data
   */
  GetChatSession(user_id: any, session_id: any, file_name: any): Observable<any> {
    const endpoint = this.apiService.getApiUrlFromConfig('GET_CHAT_SESSION');
    let data = {
      user_id: user_id,
      session_id: session_id[0],
      file_name: file_name
    };
    
    return this.apiService.post<any>(
      endpoint, 
      data, 
      this.tokenService.getAccessToken(),
      'COMMON',
      this.configdata.StreamResponseURL
    );
  }

  /**
   * Updates the chat history for a session
   * @param data - The chat history data to update
   * @returns Observable containing update response
   */
  CallUpdateSessionChatHistory(data: any): Observable<any> {
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('UPDATE_SESSION_CHAT_HISTORY'), data, this.tokenService.getAccessToken());
  }

  /**
   * Downloads a file from the server
   * @param userID - The user identifier
   * @param sessionId - The session identifier
   * @param tenantId - The tenant identifier
   * @param domainId - The domain identifier
   * @param key - The file key or identifier
   * @param isMd5HashRequired - Whether MD5 hash is required
   * @returns Observable containing file download response
   */
  DownloadFile(userID: any, sessionId: any, tenantId: any, domainId: any, key: any, isMd5HashRequired: any) {
    let data = {
      UserId: userID,
      SessionId: sessionId,
      TenantId: tenantId,
      DomainId: domainId,
      Key: key,
      IsMd5HashRequired: isMd5HashRequired
    };

    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('DOWNLOAD_FILE'), data, this.tokenService.getAccessToken())
      .pipe(
        catchError((error) => {
          this.loggerService.logError(this.ChatbotServiceError(error), "DownloadFile")
          return throwError(() => error);
        })
      );
  }

  /**
   * Gets all available domains from URF API
   * @returns Observable containing all domains data
   */
  GetURFAllDomains() {
    try {
      return this.apiService.get<any>(this.apiService.getApiUrlFromConfig('GET_URF_ALL_DOMAINS'), this.tokenService.getAccessToken(), undefined, this.apiService.getApiServiceFromConfig('GET_URF_ALL_DOMAINS'))
        .pipe(
          catchError((error: any) => {
            this.loggerService.logError(this.ChatbotServiceError(error), "GetURFAllDomains");
            return throwError(() => error);
          })
        );
    } catch (error: any) {
      this.loggerService.logError(this.ChatbotServiceError(error), "GetURFAllDomains");
      return of({});
    }
  }

  /**
   * Gets module ID for a specific domain from URF API
   * @param domain_id - The domain identifier
   * @returns Observable containing module ID data
   */
  GetURFModuleId(domain_id: number) {
    try{
    const params = new HttpParams().set('domainId', domain_id.toString());
    
    return this.apiService.get<any>(this.apiService.getApiUrlFromConfig('GET_URF_MODULE_ID'), this.tokenService.getAccessToken(), params, this.apiService.getApiServiceFromConfig('GET_URF_MODULE_ID'))
      .pipe(
        catchError((error: any) => {
          this.loggerService.logError(this.ChatbotServiceError(error), "GetURFModuleId")
          return throwError(() => error);
        })
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "GetURFModuleId")
    return of({});
  }
  }

  /**
   * Gets Freya privileges for a specific user
   * @param userId - The user identifier
   * @returns Observable containing user privileges
   */
  GetFreyaPrivileges(userId: number) {
    try{
    const payload = {
      "userId": userId.toString(),
      "resourceType": "",
      "app": "",
      "module": "",
      "subModule": "",
      "parentId": null
    };

    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GET_FREYA_PRIVILEGES'), payload, this.tokenService.getAccessToken(), this.apiService.getApiServiceFromConfig('GET_FREYA_PRIVILEGES'))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.loggerService.logError(this.ChatbotServiceError(error), "GetFreyaPrivileges")
          return throwError(() => error);
        })
      );
    }
    catch(error: any){
      this.loggerService.logError(this.ChatbotServiceError(error), "GetFreyaPrivileges")
      return of({});
    }
  }


  /**
   * Checks the health status of the dataset API
   * @returns Observable containing API health status
   */
  CheckDatasetAPIHealth() {
    const endpoint = this.apiService.getApiUrlFromConfig('CHECK_DATASET_API_HEALTH');
    return this.apiService.get<any>(
      endpoint, 
      this.tokenService.getAccessToken(), 
      undefined, 
      'COMMON', 
      this.configdata.StreamResponseURL
    ).pipe(
      map((response: any) => {
        return 200; // Successful response
      }),
      catchError((error: HttpErrorResponse) => {
        const status = error.status ? error.status.toString() : 'Unknown';

        if (!navigator.onLine) {
          this.handleError(status, "Internet connection lost. Please check your network and try again.", JSON.stringify(error), "CheckChatbotAPIHealth DS API");
          return of(309);
        } else {
          this.handleError(status, "DS API health check failed.  Please contact support team If issue persist.", JSON.stringify(error), "CheckChatbotAPIHealth DS API");
        }

        return of(500);
      })
    );
  }

  /**
   * Downloads an RTQ file by document ID
   * @param document_id - The document identifier
   * @returns Observable containing file download URL
   */
  DownloadRtqFile(document_id : string){
    try{
    const params = new HttpParams().set('documentId', document_id);
    const endpoint = this.apiService.getApiUrlFromConfig('DOWNLOAD_RTQ_FILE');
    
    return this.apiService.get<any>(
      endpoint, 
      this.tokenService.getAccessToken(), 
      params, 
      'COMMON',
      this.configdata.RtqDownloadUrl
    );
    }
    catch(error: any){
      this.loggerService.logError(this.ChatbotServiceError(error), "DownloadRtqFile")
      return of({});
    }
  }


  // Search metadata API method
  /**
   * Searches metadata with specified criteria
   * @param tenantId - The tenant identifier
   * @param searchTerm - The search term (default: empty string)
   * @param chatHistoryId - The chat history identifier
   * @param relevance - The relevance filter (default: null)
   * @param sort_by - The sort criteria (default: empty string)
   * @returns Observable containing search results
   */
  searchMetadata(tenantId: string, searchTerm: string = '', chatHistoryId: string, relevance: string | null = null, sort_by:string = ''): Observable<any> {
    try{
    const payload = {
      tenant_id: tenantId,
      search_term: searchTerm,
      response_id: chatHistoryId,
      relevance: relevance,
      sort_by: sort_by,
      sort_by_descending: true
    };
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('VIEW_SOURCE_METADATA'), payload, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "searchMetadata")
    return of({});
  }
  }

  /**
 * Unified method for shared chat operations
 * @param type - Type of operation: 'share_link' for creating share link, 'share_response' for getting shared chat
 * @param tenantId - Tenant identifier
 * @param threadId - Thread identifier
 * @param shareLink - Share link (only required for 'share_response' operation)
 */
 /**
  * Unified method for shared chat operations
  * @param type - Type of operation: 'share_link' for creating share link, 'share_response' for getting shared chat
  * @param tenantId - Tenant identifier
  * @param threadId - Thread identifier
  * @param shareLink - Share link (only required for 'share_response' operation)
  * @returns Observable containing shared chat response
  */
 sharedChat(
    type : 'share_link' | 'share_response', 
    tenantId : string, 
    threadId : string, 
    shareLink : string = ''
  ): Observable<any> {
    try{
    const basePayload = {
      tenant_id: tenantId,
      thread_id: threadId
    }
    const data = type === 'share_link'
    ? basePayload : {
      ...basePayload, share_link: shareLink
    }
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GET_SHARED_CHAT'), data, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "sharedChat")
    return of({});
  }
  }


  /**
   * Shares a conversation with specified parameters
   * @param type - Type of operation: 'share_link' or 'share_response'
   * @param tenantId - The tenant identifier
   * @param responseId - The response identifier
   * @param shareLink - The share link (default: empty string)
   * @returns Observable containing share conversation response
   */
  shareConversation(
    type : 'share_link' | 'share_response', 
    tenantId: string,
    responseId: string,
    shareLink: string = ''): Observable<any> {
    try{
    const data = type === 'share_link'
    ? {tenant_id: tenantId, response_id: responseId} 
    : {tenant_id: tenantId, response_id: responseId, share_link: shareLink}
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('SHARE_CONVERSATION'), data, this.tokenService.getAccessToken())
      .pipe(
        map(response => this.wrapResponseForCompatibility(response))
      );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "shareConversation")
    return of({});
  }
  }

  // PUT API to fetch the wizard tour status
  /**
   * Updates wizard tour settings via API
   * @param userId - The user identifier
   * @param tenantId - The tenant identifier
   * @param introductionWizardEnabled - Whether introduction wizard is enabled
   * @param tourFunctionalityEnabled - Whether tour functionality is enabled
   * @param tourCompletionStatus - Tour completion status object
   * @param wizardCompletionStatus - Wizard completion status object
   * @returns Observable containing update response
   */
  updateWizardTourAPI(userId: string, 
    tenantId: string, 
    introductionWizardEnabled: boolean,
    tourFunctionalityEnabled: boolean,
    tourCompletionStatus: {status: string},
    wizardCompletionStatus: {status: string}
  ): Observable<any> {
    try{

    // Prepare the payload
    const payload = {
        user_id: userId,
        tenant_id: tenantId,
        tour_functionality_details: {
        landing_page_tour: {
            tour_enabled: tourFunctionalityEnabled,
            tour_status: tourCompletionStatus.status,
            steps: []
        }
        },
        application_wizard_details: {
            introduction_wizard: {
                wizard_enabled: introductionWizardEnabled,
                wizard_status: wizardCompletionStatus.status,
                steps: []
            }
        },
    };

    // Make the PUT request with the payload 
    return this.apiService.put<any>(this.apiService.getApiUrlFromConfig('UPDATE_TOUR_AND_WIZARD'), payload, this.tokenService.getAccessToken())
        .pipe(
            catchError((error: HttpErrorResponse) => {
                this.loggerService.logError(this.ChatbotServiceError(error), "updateWizardTourAPI")
                return throwError(() => error);
            })
        );
      }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "updateWizardTourAPI")
    return of({});
  }
  }

  /**
   * Checks wizard tour status via API
   * @param userId - The user identifier
   * @param tenantId - The tenant identifier
   * @returns Observable containing wizard tour status
   */
  checkWizardTourAPI(userId: string, tenantId: string): Observable<any> {
    try{

    // Prepare the payload as per the cURL command
    const payload = {
        user_id: userId,
        tenant_id: tenantId
    };

    // Make the POST request with the payload
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('CHECK_WIZARD_TOUR'), payload, this.tokenService.getAccessToken())
        .pipe(
            catchError((error: HttpErrorResponse) => {
                this.loggerService.logError(this.ChatbotServiceError(error), "checkWizardTourAPI")
                return throwError(() => error);
            })
        );
      }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "checkWizardTourAPI")
    return of({});
  }
  }

  /**
   * Calls the update wizard tour API and handles the response
   * @param userId - The user identifier
   * @param tenantId - The tenant identifier
   * @param introductionWizardEnabled - Whether introduction wizard is enabled
   * @param tourFunctionalityEnabled - Whether tour functionality is enabled
   * @param tourCompletionStatus - Tour completion status object
   * @param wizardCompletionStatus - Wizard completion status object
   */
  callUpdateWizardTour(
    userId: string,
    tenantId: string,
    introductionWizardEnabled: boolean,
    tourFunctionalityEnabled: boolean,
    tourCompletionStatus: { status: string },
    wizardCompletionStatus: { status: string }) {
    this.updateWizardTourAPI(
      userId,
      tenantId,
      introductionWizardEnabled,
      tourFunctionalityEnabled,
      tourCompletionStatus,
      wizardCompletionStatus
    ).subscribe(
      response => {
        return response;
      },
      error => {
        this.loggerService.logError(this.ChatbotServiceError(error), "callUpdateWizardTour")
      }
    );
  }
  
  /**
   * Gets language preferences from the API
   * @returns Observable containing language preference data
   */
  changeLanguageAPI(): Observable<any> {
    try{
    return this.apiService.get<any>(this.apiService.getApiUrlFromConfig('CHANGE_LANGUAGE'), this.tokenService.getAccessToken(), undefined, this.apiService.getApiServiceFromConfig('CHANGE_LANGUAGE'))
      .pipe(
        map(response => ({ body: response, status: 200, statusText: 'OK' })) // Wrap response to maintain compatibility
      );
    }
    catch(error: any){
      this.loggerService.logError(this.ChatbotServiceError(error), "changeLanguageAPI")
      return of({});
    }
  }

  /**
   * Fetches response enhancement prompts for a specific language
   * @param language - The language code (e.g., 'en', 'jp', 'zhCN')
   * @returns Observable<void> that completes when prompts are loaded
   */
  fetchResponseEnhancementPrompts(language: string): Observable<void> {
    try{
    const langMap: { [key: string]: string } = {
      "en": "english",
      "jp": "japanese",
      "zhCN": "chinese"
    };

    const languageKey = langMap[language] || 'english'; // Default to english if language not found
    
    return this.configService.getFeatureConfig(this.configdata.CHATBOT_PROMPT_PREFIX_PROFILE_ID).pipe(
      map((responseData: any) => {
        // Process the response data and store enhancement prompts
        if (responseData && responseData.body && responseData.body.configuration) {
          const configuration = responseData.body.configuration;
          
          // Extract prompts for the specified language
          this.enhancementPrompts = {
            "regenerate": configuration.regenerate?.[languageKey] || "",
            "crisp": configuration.crisp?.[languageKey] || "",
            "detailed": configuration.detailed?.[languageKey] || ""
          };
        } else {
          // If the response is not successful, store an empty object
          this.enhancementPrompts = {
            "regenerate": "",
            "crisp": "",
            "detailed": ""
          };
        }
      }),
      catchError(error => {
        this.loggerService.logError(this.ChatbotServiceError(error), "fetchResponseEnhancementPrompts")
        // Store an empty object on error
        this.enhancementPrompts = {
          "regenerate": "",
          "crisp": "",
          "detailed": ""
        };
        return of(); // Return an empty observable
      })
    );
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "fetchResponseEnhancementPrompts")
    return of();
  }
  }

  /**
   * Generates RegRadar questions via API
   * @param userId - The user identifier
   * @param tenantId - The tenant identifier
   * @param domainId - The domain identifier
   * @param sessionId - The session identifier
   * @param domainName - The domain name
   * @param moduleName - The module name
   * @returns Observable containing generated RegRadar questions
   */
  generateRegRadarAPI(userId: string, tenantId: string, domainId: string, sessionId: string, domainName: string, moduleName: string): Observable<any> {
    try{
    const payload = {
      tenant_id: tenantId,
      domain_id: domainId,
      module_name: moduleName.toLowerCase(),
      domain_name: domainName,
      user_id: userId,
      session_id: sessionId,
      languages:  [
        "english",
        "japanese",
        "chinese"
      ]
  }
  
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GENERATE_REGRADAR_QUESTIONS'), payload, this.tokenService.getAccessToken())
      .pipe(
        catchError(error => {
          this.loggerService.logError(`Error in generateRegRadarAPI: ${JSON.stringify(error?.message)}`, "generateRegRadarAPI")
          return throwError(() => error);
        })
      );
    }
    catch(error: any){
      this.loggerService.logError(this.ChatbotServiceError(error), "generateRegRadarAPI")
      return of({});
    }
  }

  /**
   * Agentic UI (v3) RegRadar generate — body matches POST /v2/api/generate-regradar-questions:
   * { tenant_id, module_name, domain_name, languages }. Does not alter the v2 generateRegRadarAPI payload.
   */
  generateRegRadarAPIForAgenticUI(tenantId: string, domainName: string, moduleName: string): Observable<any> {
    try {
      const payload = {
        tenant_id: tenantId,
        module_name: moduleName.toLowerCase(),
        domain_name: domainName,
        languages: [
          'english',
          'japanese',
          'chinese'
        ]
      };

      return this.apiService.post<any>(
        this.apiService.getApiUrlFromConfig('GENERATE_REGRADAR_QUESTIONS'),
        payload,
        this.tokenService.getAccessToken()
      ).pipe(
        catchError(error => {
          this.loggerService.logError(
            `Error in generateRegRadarAPIForAgenticUI: ${JSON.stringify(error?.message)}`,
            'generateRegRadarAPIForAgenticUI'
          );
          return throwError(() => error);
        })
      );
    } catch (error: any) {
      this.loggerService.logError(this.ChatbotServiceError(error), 'generateRegRadarAPIForAgenticUI');
      return of({});
    }
  }

  /**
   * Gets RegRadar questions from API
   * @param userId - The user identifier
   * @param tenantId - The tenant identifier
   * @param domainName - The domain name
   * @param moduleName - The module name
   * @returns Observable containing RegRadar questions data
   */
  getRegRadarAPI(userId: string, tenantId: string, domainName: string, moduleName: string): Observable<any> {
  try {
    // Build query parameters
    const params = new HttpParams()
      .set('user_id', userId)
      .set('tenant_id', tenantId)
      .set('domain', domainName)
      .set('module', moduleName.toLowerCase());

    // Use the new ApiService template with GET method
    return this.apiService.get<any>(this.apiService.getApiUrlFromConfig('GET_REGRADAR_QUESTIONS'), this.tokenService.getAccessToken(), params)
      .pipe(
        catchError(error => {
          this.loggerService.logError(this.ChatbotServiceError(error), "getRegRadarAPI")
          // Return the mock response in case of an error
          const fallbackErrorResponse = {
            "status": "fail",
            "message": "Failed to retrieve questions from RegRadar database and AppConfig",
            "reg_radar_data": [],
            "app_config_data": []
          };
          return of(fallbackErrorResponse);
        })
      );
  } catch (error: any) {
    this.loggerService.logError(this.ChatbotServiceError(error), "getRegRadarAPI")
    return of({});
  }
}
  /**
   * Searches news feeds with specified criteria
   * @param userId - The user identifier
   * @param tenantId - The tenant identifier
   * @param domainId - The domain identifier
   * @param newsFeedsIds - Array of news feed identifiers
   * @param searchTerm - The search term
   * @param sortBy - The sort criteria
   * @returns Observable containing search results
   */
  searchNewsFeeds(userId:string, tenantId:string, domainId:string, newsFeedsIds: any, searchTerm: string, sortBy: string):Observable<any>{
    try{
    const body = {
        "user_id": userId,
        "tenant_id": tenantId,
        "domain_id": domainId,
        "news_feeds_ids": newsFeedsIds,
        "search_filters": {
            "search_term": searchTerm
        },
        "sort_options": {
            "sort_by": sortBy
        },
        "page": 1,
        "page_size": 100
      };
      return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('SEARCH_NEWS_FEEDS'), body, this.tokenService.getAccessToken())
    }
    catch(error: any){
      this.loggerService.logError(this.ChatbotServiceError(error), "searchNewsFeeds")
      return of({});
    }
  }

  /**
   * Gets all user domains from API
   * @param tenantId - The tenant identifier
   * @param userId - The user identifier
   * @param moduleName - The module name
   * @returns Observable containing user domains data
   */
  getAllUserDomainsAPI(tenantId: string, userId: string, moduleName: string): Observable<any> {
    try{
    const payload = {
      tenant_id: tenantId,
      user_id: userId,
      module_name: moduleName
    };
    return this.apiService.post<any>(this.apiService.getApiUrlFromConfig('GET_USER_PRIVILEGES'), payload, this.tokenService.getAccessToken());
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "getAllUserDomainsAPI")
    return of({});
  }
  }

  /**
   * Maps modules to accessible domains based on API response
   * @param apiResponse - Response from getAllUserDomains API
   * @returns Object mapping module names to their accessible domain aliases
   */
  mapDomainsToModules(apiResponse: any): { accessibleDomains: string[], moduleDomainsMap: { [moduleName: string]: string[] } } {
    try{
    const moduleDomainsMap: { [moduleName: string]: string[] } = {};
    
    if (!apiResponse?.data?.domains || !apiResponse?.data?.permissions) {
      return { accessibleDomains: [], moduleDomainsMap: {} };
    }

    // Get accessible domains from the API response and extract aliases
    const fullDomainNames = apiResponse.data.domains;
    const accessibleDomains = fullDomainNames.map((domainName: string) => this.extractDomainAlias(domainName));
    
    // Process permissions to map domains to modules based on displayName
    const permissions = apiResponse.data.permissions;

    permissions.forEach((permission: any) => {
      const displayName = permission.displayName?.toLowerCase() || '';
      const moduleName = permission.moduleName;
      
      // Initialize module entry if it doesn't exist
      if (!moduleDomainsMap[moduleName]) {
        moduleDomainsMap[moduleName] = [];
      }
      
      // Check which domain this permission belongs to based on displayName
      fullDomainNames.forEach((domainName: string) => {
        const domainAlias = this.extractDomainAlias(domainName);
        
        // Check if displayName contains the domain alias (case insensitive)
        if (displayName.includes(domainAlias.toLowerCase())) {
          // Add domain alias to this module if not already present
          if (!moduleDomainsMap[moduleName].includes(domainAlias)) {
            moduleDomainsMap[moduleName].push(domainAlias);
          }
        }
      });
    });

    return { accessibleDomains, moduleDomainsMap };
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotServiceError(error), "mapDomainsToModules")
    return { accessibleDomains: [], moduleDomainsMap: {} };
  }
  }

  /**
   * Extracts domain alias from full domain name
   * @param domainName - Full domain name like "Medicinal Products (MPR)"
   * @returns Domain alias like "MPR"
   */
  private extractDomainAlias(domainName: string): string {
    // Safe regex with bounded quantifier
    const match = domainName.match(/\(([^)]{1,200})\)$/);
    return match ? match[1] : domainName;
  }
  
}


